import { useState } from "react";

/* =========================================================================
 * MCQActivity — multiple choice question with instant feedback
 * ========================================================================= */
export function MCQActivity({ question, options, correctIndex, explanation, onAnswer, compact = false }) {
  const [picked, setPicked] = useState(null);
  const reveal = picked !== null;

  const pick = (i) => {
    if (reveal) return;
    setPicked(i);
    onAnswer && onAnswer(i === correctIndex);
  };

  return (
    <div className="activity mcq" data-testid="activity-mcq">
      <div className="activity-question">{question}</div>
      <div className="mcq-options">
        {options.map((opt, i) => {
          const isCorrect = i === correctIndex;
          const isPicked = i === picked;
          let cls = "mcq-option";
          if (reveal) {
            if (isCorrect) cls += " correct";
            else if (isPicked) cls += " wrong";
            else cls += " dim";
          }
          return (
            <button
              key={i}
              className={cls}
              onClick={() => pick(i)}
              disabled={reveal}
              data-testid={`mcq-option-${i}`}
            >
              <span className="mcq-marker">{["أ", "ب", "ج", "د", "هـ"][i] || i + 1}</span>
              <span className="mcq-text">{opt}</span>
              {reveal && isCorrect && <span className="mcq-icon">✓</span>}
              {reveal && isPicked && !isCorrect && <span className="mcq-icon">✕</span>}
            </button>
          );
        })}
      </div>
      {reveal && (
        <div className={`activity-feedback ${picked === correctIndex ? "good" : "bad"}`} data-testid="mcq-feedback">
          <strong>{picked === correctIndex ? "✓ إجابة صحيحة" : "✕ ليست الإجابة الصحيحة"}</strong>
          {explanation && <div style={{ marginTop: 6 }}>{explanation}</div>}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * MatchActivity — click two items to pair them (mobile-friendly, no drag)
 * ========================================================================= */
export function MatchActivity({ instruction, pairs, onComplete }) {
  // Shuffle right column once per mount
  const [leftPicked, setLeftPicked] = useState(null);
  const [rightPicked, setRightPicked] = useState(null);
  const [matched, setMatched] = useState({}); // {leftIndex: rightIndex}
  const [wrongFlash, setWrongFlash] = useState(null);

  const lefts = pairs.map((p, i) => ({ text: p.left, i }));
  const [rights] = useState(() => {
    const arr = pairs.map((p, i) => ({ text: p.right, i }));
    for (let k = arr.length - 1; k > 0; k--) {
      const j = Math.floor(Math.random() * (k + 1));
      [arr[k], arr[j]] = [arr[j], arr[k]];
    }
    return arr;
  });

  const tryPair = (li, ri) => {
    if (li === ri) {
      const next = { ...matched, [li]: ri };
      setMatched(next);
      setLeftPicked(null);
      setRightPicked(null);
      if (Object.keys(next).length === pairs.length) onComplete && onComplete(true);
    } else {
      setWrongFlash({ li, ri });
      setTimeout(() => {
        setWrongFlash(null);
        setLeftPicked(null);
        setRightPicked(null);
      }, 700);
    }
  };

  const handleLeft = (i) => {
    if (matched[i] !== undefined) return;
    setLeftPicked(i);
    if (rightPicked !== null) tryPair(i, rightPicked);
  };
  const handleRight = (i) => {
    if (Object.values(matched).includes(i)) return;
    setRightPicked(i);
    if (leftPicked !== null) tryPair(leftPicked, i);
  };

  const done = Object.keys(matched).length === pairs.length;

  return (
    <div className="activity match" data-testid="activity-match">
      <div className="activity-question">{instruction}</div>
      <div className="match-cols">
        <div className="match-col">
          {lefts.map((l) => {
            const isMatched = matched[l.i] !== undefined;
            const isPicked = leftPicked === l.i;
            const isWrong = wrongFlash && wrongFlash.li === l.i;
            let cls = "match-item";
            if (isMatched) cls += " ok";
            else if (isPicked) cls += " picked";
            if (isWrong) cls += " wrong-flash";
            return (
              <button
                key={l.i}
                className={cls}
                onClick={() => handleLeft(l.i)}
                disabled={isMatched}
                data-testid={`match-left-${l.i}`}
              >
                {l.text}
              </button>
            );
          })}
        </div>
        <div className="match-col">
          {rights.map((r) => {
            const isMatched = Object.values(matched).includes(r.i);
            const isPicked = rightPicked === r.i;
            const isWrong = wrongFlash && wrongFlash.ri === r.i;
            let cls = "match-item";
            if (isMatched) cls += " ok";
            else if (isPicked) cls += " picked";
            if (isWrong) cls += " wrong-flash";
            return (
              <button
                key={r.i}
                className={cls}
                onClick={() => handleRight(r.i)}
                disabled={isMatched}
                data-testid={`match-right-${r.i}`}
              >
                {r.text}
              </button>
            );
          })}
        </div>
      </div>
      {done && (
        <div className="activity-feedback good" data-testid="match-done">
          ✓ أحسنت! جميع الأزواج صحيحة.
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * OrderActivity — click items in correct order
 * ========================================================================= */
export function OrderActivity({ instruction, items, correctOrder, onComplete }) {
  const [picks, setPicks] = useState([]);

  const click = (i) => {
    if (picks.includes(i)) return;
    const next = [...picks, i];
    setPicks(next);
    if (next.length === items.length) {
      const ok = next.every((v, idx) => v === correctOrder[idx]);
      onComplete && onComplete(ok);
    }
  };

  const reset = () => setPicks([]);
  const done = picks.length === items.length;
  const correct = done && picks.every((v, idx) => v === correctOrder[idx]);

  return (
    <div className="activity order" data-testid="activity-order">
      <div className="activity-question">{instruction}</div>
      <div className="order-items">
        {items.map((it, i) => {
          const pickedIdx = picks.indexOf(i);
          const isPicked = pickedIdx !== -1;
          return (
            <button
              key={i}
              className={`order-item ${isPicked ? "picked" : ""}`}
              onClick={() => click(i)}
              disabled={done || isPicked}
              data-testid={`order-item-${i}`}
            >
              {isPicked && <span className="order-rank">{pickedIdx + 1}</span>}
              <span>{it}</span>
            </button>
          );
        })}
      </div>
      {done && (
        <div className={`activity-feedback ${correct ? "good" : "bad"}`} data-testid="order-feedback">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <strong>{correct ? "✓ ترتيب صحيح!" : "✕ الترتيب غير صحيح"}</strong>
            <button className="btn btn-ghost" style={{ padding: "6px 12px", fontSize: 13 }} onClick={reset}>
              أعد المحاولة
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * FillBlankActivity — fill the missing word
 * ========================================================================= */
export function FillBlankActivity({ instruction, before, blankAnswer, after, hint, onComplete }) {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const normalized = (s) => (s || "").trim().replace(/[ًٌٍَُِّْ]/g, "");

  const submit = () => {
    if (!value.trim() || submitted) return;
    setSubmitted(true);
    onComplete && onComplete(normalized(value) === normalized(blankAnswer));
  };

  const correct = submitted && normalized(value) === normalized(blankAnswer);

  return (
    <div className="activity fill" data-testid="activity-fill">
      <div className="activity-question">{instruction}</div>
      <div className="fill-line">
        <span>{before}</span>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="..."
          disabled={submitted}
          className={`fill-input ${submitted ? (correct ? "ok" : "bad") : ""}`}
          data-testid="fill-input"
        />
        <span>{after}</span>
      </div>
      {hint && !submitted && <div className="hint">💡 {hint}</div>}
      {!submitted ? (
        <button className="btn btn-primary" onClick={submit} disabled={!value.trim()} data-testid="fill-submit-btn" style={{ marginTop: 10 }}>
          تحقّق
        </button>
      ) : (
        <div className={`activity-feedback ${correct ? "good" : "bad"}`} data-testid="fill-feedback">
          {correct ? "✓ إجابة صحيحة!" : `✕ الإجابة الصحيحة: «${blankAnswer}»`}
        </div>
      )}
    </div>
  );
}

/* =========================================================================
 * Dispatch — render any activity by type
 * ========================================================================= */
export default function Activity({ activity, onAnswer }) {
  if (!activity || !activity.type) return null;
  switch (activity.type) {
    case "mcq":
      return (
        <MCQActivity
          question={activity.question}
          options={activity.options}
          correctIndex={activity.correct_index}
          explanation={activity.explanation}
          onAnswer={onAnswer}
        />
      );
    case "match":
      return (
        <MatchActivity
          instruction={activity.instruction}
          pairs={activity.pairs}
          onComplete={onAnswer}
        />
      );
    case "order":
      return (
        <OrderActivity
          instruction={activity.instruction}
          items={activity.items}
          correctOrder={activity.correct_order}
          onComplete={onAnswer}
        />
      );
    case "fill":
      return (
        <FillBlankActivity
          instruction={activity.instruction}
          before={activity.before}
          blankAnswer={activity.blank_answer}
          after={activity.after}
          hint={activity.hint}
          onComplete={onAnswer}
        />
      );
    default:
      return null;
  }
}
